import "@fontsource-variable/inter";
import "@fontsource-variable/jetbrains-mono";
import "./styles/tokens.css";
import "./styles/base.css";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { App } from "./app/App";
import { ErrorScreen } from "./app/errors/ErrorScreen";
import { applyTheme } from "./app/theme";
import { loadEnv } from "./config/env";
import type { IdentityServices } from "./data/identity/context";
import { createHttp } from "./data/identity/live/http";
import { createLiveServices } from "./data/identity/live/services";
import { consumeDevSessionParam } from "./data/identity/mock/dev-session";
import { createSessionStore } from "./data/identity/session-store";
import { createMockBackend } from "./data/mock";
import { onCacheCleared } from "./data/resource";
import { tabClipboard } from "./features/draft/clipboard";
import { countProjectFiles } from "./data/files";
import { createLiveProjectsApi } from "./data/projects/live";
import { withRealFileCount } from "./data/projects/real-file-count";

applyTheme();
// Sign-out, or another person signing in, forgets every drawing copy in every tab: copies never cross people.
onCacheCleared(() => void tabClipboard().clear());

const container = document.getElementById("root");
if (container === null) throw new Error('index.html is missing <div id="root">.');
const root = createRoot(container);

const MS_PER_SECOND = 1_000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE;

async function start(): Promise<void> {
  const env = loadEnv();
  const now = (): number => Date.now();
  const checkIntervalMs = env.VITE_SESSION_CHECK_SECONDS * MS_PER_SECOND;
  // Live identity (D-026; RFC 0001 §5): sign-in through the identity service. Projects come from its projects API
  // with VITE_PROJECTS_MODE=live (WS-29); otherwise the projects mock follows its session.
  const live =
    env.VITE_IDENTITY_MODE === "live"
      ? createLiveServices({
          apiPath: env.VITE_IDENTITY_API_PATH,
          checkIntervalMs,
          now,
          projectPrefix: env.VITE_DEV_ORG_PROJECT_PREFIX,
        })
      : undefined;
  const backend = await createMockBackend({
    latencyMs: env.VITE_MOCK_LATENCY_MS,
    limits: {
      idleMs: env.VITE_SESSION_IDLE_MINUTES * MS_PER_MINUTE,
      absoluteMs: env.VITE_SESSION_ABSOLUTE_DAYS * MS_PER_DAY,
    },
    demoOrg: { name: env.VITE_DEV_ORG_NAME, projectPrefix: env.VITE_DEV_ORG_PROJECT_PREFIX },
    session: live?.port,
  });
  const api =
    env.VITE_PROJECTS_MODE === "live" && live !== undefined
      ? createLiveProjectsApi({
          http: createHttp(env.VITE_IDENTITY_API_PATH, (input, init) => fetch(input, init)),
          session: live.port,
          members: () => live.identity.listMembers(),
          fileCount: countProjectFiles,
        })
      : withRealFileCount(backend.api, countProjectFiles);
  let identity: IdentityServices;
  if (live === undefined) {
    // Mock only: ?dev-session=asha signs in before the first render and leaves the address bar clean.
    const devSession = consumeDevSessionParam();
    if (devSession !== undefined && !backend.startDevSession(devSession))
      console.warn(`Unknown dev-session "${devSession}".`);
    const session = createSessionStore(backend.identity, { checkIntervalMs, now });
    identity = { identity: backend.identity, session, staff: backend.staff, demo: backend.demo };
  } else {
    identity = {
      identity: live.identity,
      session: live.session,
      staff: undefined,
      demo: undefined,
    };
  }
  root.render(
    <StrictMode>
      <App api={api} identity={identity} />
    </StrictMode>,
  );
}

start().catch((error: unknown) => {
  console.error("Orthos could not start", error);
  root.render(
    <ErrorScreen
      title="Orthos could not start"
      detail={error instanceof Error ? error.message : String(error)}
    />,
  );
});
